import type { Hold } from '../../../types/climb';
import type { DetectedHold, ScanSensitivity } from '../../../types/climb-scan';
import { boundingBoxOf } from '../../../types/climb-scan';
import { PHOTO_HOLD_ROLE_COLORS, circularHold } from '../../../components/climbs/photo-hold-roles';
import { isRectangle, outlinedHold, type OutlinePoint } from '../../../components/climbs/hold-outline';

/** The analysis reports on a 0-1000 grid, whatever the photo's size. */
export const BOX_GRID = 1000;

/** Nothing useful can be circled below this, in natural pixels. */
const MIN_BOX_RADIUS_PX = 8;

/**
 * How close two marks have to be to be the same hold, as a fraction of the
 * photo. A placeholder and the mark taken from it sit on exactly the same
 * spot; this is only slack for the rounding between the two coordinate spaces.
 */
const SAME_HOLD = 0.002;

/**
 * **The one place the two coordinate spaces meet.** A detection is reported on
 * a 0-1000 grid because the detector does not know the photo's pixel size; a
 * climb's marks are fractions of a decoded image. Everything below converts
 * from the first to the second, and nothing else in the app does.
 */
function toFractions(outline: Array<[number, number]>): OutlinePoint[] {
  return outline.map(([x, y]) => [x / BOX_GRID, y / BOX_GRID]);
}

/**
 * The circles a scan starts from: one per hold the analysis found in this
 * colour, already on the photo so the setter corrects them rather than drawing
 * the whole climb by hand.
 *
 * **The highest hold is the top.** It is the one thing about a climb that can be
 * read off a photo with any confidence — you top out at the top — so it is
 * marked red and everything below it is left as plain holds for the setter to
 * qualify. Same rule, same two colours as the member app's editor.
 *
 * **A rectangle is not a shape.** A detector that answers with a box per hold
 * has said where the hold is and nothing about what it looks like, so a box goes
 * on being drawn as the circle it has always been drawn as — sized to the box's
 * longer side, since a circle that fitted inside it would leave part of the hold
 * outside. Only a real outline is carried onto the hold as one, which is what a
 * segmentation model gives and what the editor and every renderer are now ready
 * for.
 */
export function holdsFromDetections(
  detections: DetectedHold[],
  naturalWidth: number,
  naturalHeight: number
): Hold[] {
  if (detections.length === 0 || naturalWidth <= 0 || naturalHeight <= 0) return [];

  const boxes = detections.map((detection) => boundingBoxOf(detection.outline));
  const topIndex = boxes.reduce(
    (highest, box, index) => (box.y < boxes[highest].y ? index : highest),
    0
  );

  return detections.map((detection, index) => {
    const color =
      index === topIndex ? PHOTO_HOLD_ROLE_COLORS.finish : PHOTO_HOLD_ROLE_COLORS.hand;

    if (!isRectangle(detection.outline)) {
      return outlinedHold(toFractions(detection.outline), color);
    }

    const box = boxes[index];
    const widthPx = (box.width / BOX_GRID) * naturalWidth;
    const heightPx = (box.height / BOX_GRID) * naturalHeight;
    const radiusPx = Math.max(widthPx, heightPx) / 2;

    return circularHold(
      (box.x + box.width / 2) / BOX_GRID,
      (box.y + box.height / 2) / BOX_GRID,
      Math.max(radiusPx, MIN_BOX_RADIUS_PX) / naturalHeight,
      color,
      naturalWidth,
      naturalHeight
    );
  });
}

/**
 * Every hold a scan found, as one pool of placeholders — in the scans panel and
 * in the member app's climb editor alike.
 *
 * **No roles on the wall itself.** `holdsFromDetections` guesses a top, which is
 * the right guess once a climb is being drawn and a meaningless one here: a wall
 * has no start and no top. They are all plain holds until a climb is made of
 * them, and the guess moves to that moment.
 */
export function wallPlaceholders(
  detections: DetectedHold[],
  naturalWidth: number,
  naturalHeight: number
): Hold[] {
  return holdsFromDetections(detections, naturalWidth, naturalHeight).map((hold) => ({
    ...hold,
    color: PHOTO_HOLD_ROLE_COLORS.hand,
  }));
}

/**
 * Whether a placeholder has been taken into one of these marks.
 *
 * Matched by where they sit rather than by identity: a hold taken into a climb
 * becomes a mark of its own and may since have been repainted or renumbered,
 * but it is still in the same place on the wall.
 */
export function isPlaceholderTaken(placeholder: Hold, holds: Hold[]): boolean {
  return holds.some(
    (hold) =>
      Math.abs(hold.x - placeholder.x) < SAME_HOLD && Math.abs(hold.y - placeholder.y) < SAME_HOLD
  );
}

/**
 * The placeholders none of these marks has taken — the ones still offered while
 * a climb is being made of the others. A placeholder left underneath a hold that
 * has been taken would be a second, invisible target on the same plastic.
 */
export function untakenPlaceholders(placeholders: Hold[], holds: Hold[]): Hold[] {
  if (holds.length === 0) return placeholders;

  return placeholders.filter((placeholder) => !isPlaceholderTaken(placeholder, holds));
}

/**
 * Under this score a detected hold is a **maybe**: drawn on the wall, but
 * fainter and dotted, and never pre-selected in a proposal (boardsesh's editor
 * rule, #317). Every detector that scores — SAM 3 and the local model alike,
 * both 0–1 — and Gemini, which does not score, never has one.
 *
 * **0.6, not boardsesh's 0.75**, measured on the bench
 * (`climb_scans:rfdetr_bench`, gestures saved with the maybes left
 * unselected): 0.6 saved the most for both detectors (local 55.8%, SAM 3
 * 68.0%) against 54.1% and 64.5% at 0.75. A maybe on the climb costs a tap; a
 * maybe off it costs nothing, because it was never selected.
 */
export const MAYBE_BELOW = 0.6;

export function isMaybe(detection: DetectedHold): boolean {
  return typeof detection.score === 'number' && detection.score < MAYBE_BELOW;
}

/**
 * Whether a setter hid this detection with the confidence slider: scored under
 * `minScore`. A detection with no score (Gemini) is never hidden.
 */
export function isHidden(detection: DetectedHold, minScore: number | null | undefined): boolean {
  return minScore != null && typeof detection.score === 'number' && detection.score < minScore;
}

/**
 * The places, in `detections`, of the holds a wall shows.
 *
 * - **Excluded by hand** (`excluded`, the scan's `excluded_holds`): never.
 * - **The slider decides where a setter moved it** (`minScore`, the scan's
 *   `min_score`): everything scored under it is hidden.
 * - Where nobody did, how hard the wall was read decides: a scan read at
 *   "fewer" asked for only what the detector was sure of, so its maybes are
 *   hidden; "normal" and "more" show them, dotted.
 *
 * Hidden, never deleted: undoing either brings them back. Places rather than
 * holds because that is what an exclusion is written as.
 */
export function shownIndices(
  detections: DetectedHold[],
  sensitivity: ScanSensitivity | null | undefined,
  minScore: number | null = null,
  excluded: number[] = []
): number[] {
  const out = new Set(excluded);

  return detections.flatMap((detection, index) => {
    if (out.has(index)) return [];
    if (minScore != null) return isHidden(detection, minScore) ? [] : [index];

    return sensitivity === 'fewer' && isMaybe(detection) ? [] : [index];
  });
}

/** The holds a wall shows — see `shownIndices`. */
export function shownDetections(
  detections: DetectedHold[],
  sensitivity: ScanSensitivity | null | undefined,
  minScore: number | null = null,
  excluded: number[] = []
): DetectedHold[] {
  return shownIndices(detections, sensitivity, minScore, excluded).map((index) => detections[index]);
}

/** The slider's steps, and the highest it goes: a wall is never hidden whole. */
export const MIN_SCORE_STEP = 0.05;
export const MIN_SCORE_MAX = 0.95;

/**
 * Where the confidence slider starts: the lowest score on the wall, rounded
 * down to a step — under it there is nothing to hide. Null when nothing on the
 * wall is scored, and there is no slider.
 */
export function minScoreFloor(detections: DetectedHold[]): number | null {
  const scores = detections.flatMap((detection) =>
    typeof detection.score === 'number' ? [detection.score] : []
  );
  if (scores.length === 0) return null;

  const steps = Math.floor(Math.min(...scores) / MIN_SCORE_STEP + 1e-9);
  return Math.min(steps * MIN_SCORE_STEP, MIN_SCORE_MAX);
}

/** The places of the maybes in a list of detections, for the canvas. */
export function maybeIndices(detections: DetectedHold[]): number[] {
  return detections.flatMap((detection, index) => (isMaybe(detection) ? [index] : []));
}
